/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {
    getLocaleByReference,
    getParamsFromPath,
    getDefaultSite,
    getSiteByReference
} from '@salesforce/retail-react-app/app/utils/site-utils'
import {HOME_HREF, urlPartPositions} from '@salesforce/retail-react-app/app/constants'

/**
 * Constructs an absolute URL from a given path and an optional application origin.
 *
 * @param {string} path - The relative URL path to be appended to the origin.
 * @param {string} [appOrigin] - The optional application origin (e.g., "https://example.com").
 *                                If not provided, the function will call `getAppOrigin()`.
 * @returns {string} - The fully qualified URL as a string.
 */
export const absoluteUrl = (path, appOrigin) => {
    return new URL(path, appOrigin || getAppOrigin()).toString()
}

/**
 * Modifies a given url by adding/updating query parameters.
 *
 * @param {string} url - The base url of the output url set.
 * @param {object} extraParams - A key values pairing used to add static search param values.
 * @returns {string} A URL with additional params
 * @example
 * import {rebuildPathWithParams} from '/path/to/utils/url'
 *
 * rebuildPathWithParams(
 *     '/en-GB/product/25501032M',
 *     {color: 'JJ2SKXX', size: 'MD'}
 * )
 *
 * // Returns
 * // '/en-GB/product/25501032M?color=JJ2SKXX&size=9MD'
 */
export const rebuildPathWithParams = (url, extraParams) => {
    const [pathname, search] = url.split('?')
    const params = new URLSearchParams(search)

    updateSearchParams(params, extraParams)

    // Clean up any trailing `=` for params without values.
    const paramStr = params.toString().replace(/=&/g, '&').replace(/=$/, '')

    // Generate the newly updated url.
    return `${pathname}${Array.from(paramStr).length > 0 ? `?${paramStr}` : ''}`
}

export const updateSearchParams = (searchParams, newParams) => {
    Object.entries(newParams).forEach(([key, value]) => {
        // 0 is a valid value as for a param
        if (!value && value !== 0) {
            searchParams.delete(key)
        } else {
            searchParams.set(key, value)
        }
    })
}

/**
 * Builds a list of modified Urls with the provided params key and values,
 * preserving any search params provided in the original url.Optionally
 * you can pass and object used to set static params values.
 * @param {string} url - The base url of the output url set.
 * @param {string} key - The search params for the associated values
 * @param {Array} values - The search param values
 * @param {object} extraParams - A key values pairing used to add static search param values.
 * @returns {string[]} A list of URLs
 * @example
 * import {buildUrlSet} from '/path/to/utils/url'
 *
 * buildUrlSet(
 *     '/womens/clothing',
 *     'sort',
 *     ['price-high-to-low', 'price-low-to-high'],
 *     {offset: 0}
 * )
 *
 * // Returns
 * // ['/womens/clothing?sort=price-high-to-low', '/womens/clothing?sort=price-low-to-high']
 */
export const buildUrlSet = (url = '', key = '', values = [], extraParams = {}) =>
    values.map((value) => rebuildPathWithParams(url, {[key]: value, ...extraParams}))

/**
 * Given a category returns an href to the product list page.
 *
 * @param {Object} category - The category object
 * @param {Object} categoryUrlMap - Optional map of categoryId -> c_relativeUrl from useCategoryUrlMap()
 * @returns {string}
 */
export const categoryUrlBuilder = (category, categoryUrlMap = {}) => {
    // First try the category's own c_relativeUrl
    if (category?.c_relativeUrl) {
        return encodeURI(category.c_relativeUrl)
    }

    // Then try the categoryUrlMap lookup
    if (categoryUrlMap && category?.id && categoryUrlMap[category.id]) {
        return encodeURI(categoryUrlMap[category.id])
    }

    // No URL available - return null instead of a broken fallback
    return null
}

const productNameToSlug = (productName) =>
    String(productName)
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['\u2019]/g, '')
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')

/**
 * Given a product and the current locale returns an href to the product detail page.
 *
 * @param {Object} product
 * @returns {string}
 */
export const productUrlBuilder = function (product) {
    if (product?.c_relativeUrl) {
        return encodeURI(product.c_relativeUrl)
    }

    const productId = product?.productId || product?.id
    if (!productId) {
        return null
    }

    const productName = product?.productName || product?.name || productId
    const productNameSlug = productNameToSlug(productName) || productId
    return `/p/${encodeURIComponent(productNameSlug)}/${encodeURIComponent(productId)}.html`
}

/**
 * Given a search term, constructs a search url.
 * Optional extra params (e.g. searchSource) are forwarded to /api/product-search-ssr and can trigger custom server logic before SCAPI.
 *
 * @param {string} searchTerm
 * @param {Record<string, string>} [extraParams] - Optional query params (e.g. { searchSource: 'promo' })
 * @param {function} [buildUrl] - Optional PWA Kit URL builder for active site/locale routing.
 * @returns {string}
 */
export const searchUrlBuilder = (searchTerm, extraParams = {}, buildUrl) => {
    const base = '/search?q=' + encodeURIComponent(searchTerm)
    if (!extraParams || Object.keys(extraParams).length === 0) {
        return buildUrl ? buildUrl(base) : base
    }
    const params = new URLSearchParams(extraParams)
    const url = `${base}&${params.toString()}`
    return buildUrl ? buildUrl(url) : url
}

/**
 * Returns a relative URL for a locale short code.
 * Based on your app configuration, this function will replace your current locale shortCode with a new one
 *
 * @param {String} shortCode - The locale short code.
 * @param {function(*, *, *, *=): string} - Generates a site URL from the provided path, site and locale.
 * @param {string[]} opts.disallowParams - URL parameters to remove
 * @param {Object} opts.location - location object to replace the default `window.location`
 * @returns {String} url - The relative URL for the specific locale.
 */
export const getPathWithLocale = (shortCode, buildUrl, opts = {}) => {
    const location = opts.location ? opts.location : window.location
    let {siteRef, localeRef} = getParamsFromPath(`${location.pathname}${location.search}`)
    let {pathname, search} = location

    // sanitize the site from current url if existing
    if (siteRef) {
        pathname = pathname.replace(`/${siteRef}`, '')
        search = search.replace(`site=${siteRef}`, '')
    }
    // sanitize the locale from current url if existing
    if (localeRef) {
        pathname = pathname.replace(`/${localeRef}`, '')
        search = search.replace(`locale=${localeRef}`, '')
    }
    // remove ending any &
    search = search.replace(/&$/, '')

    // Remove query parameters
    const {disallowParams = []} = opts

    let queryString = new URLSearchParams(`${search}`)

    if (disallowParams.length) {
        disallowParams.forEach((param) => {
            queryString.delete(param)
        })
    }

    const site = getSiteByReference(siteRef)
    const locale = getLocaleByReference(site, shortCode)

    // rebuild the url with new locale,
    const newUrl = buildUrl(
        `${pathname}${Array.from(queryString).length !== 0 ? `?${queryString}` : ''}`,
        // By default, as for home page, when the values of site and locale belongs to the default site,
        // they will be not shown in the url just
        site.alias || site.id,
        locale?.alias || locale?.id
    )
    return newUrl
}

/**
 * Generates the URL Template literal (Template string) used to build URLs in the App according
 * the current selected site/locale and the default App URL configuration.
 *
 * @param appConfig Application default configuration.
 * @param siteRef Current selected Site reference. The value can be the Site id or alias.
 * @param localeRef Current selected Locale reference. The value can be the Locale id or alias.
 * @returns {function(*, *, *): string} function providing: path, site and locale generates a URL.
 */
export const createUrlTemplate = (appConfig, siteRef, localeRef) => {
    const {site: siteConfig, locale: localeConfig, showDefaults: showDefaultsConfig} = appConfig.url
    const defaultSite = getDefaultSite()
    const site = getSiteByReference(siteRef)
    const defaultLocale = getLocaleByReference(site, site.l10n.defaultLocale)

    const isDefaultSite =
        defaultSite.id === siteRef || (defaultSite.alias && defaultSite.alias === siteRef)
    const isDefaultLocale =
        defaultLocale.id === localeRef || (defaultLocale.alias && defaultLocale.alias === localeRef)

    const querySite =
        (siteConfig === urlPartPositions.QUERY_PARAM && showDefaultsConfig) ||
        (siteConfig === urlPartPositions.QUERY_PARAM && !showDefaultsConfig && !isDefaultSite)
    const queryLocale =
        (localeConfig === urlPartPositions.QUERY_PARAM && showDefaultsConfig) ||
        (localeConfig === urlPartPositions.QUERY_PARAM && !showDefaultsConfig && !isDefaultLocale)

    const isQuery = querySite || queryLocale

    const pathSite =
        (siteConfig === urlPartPositions.PATH && showDefaultsConfig) ||
        (siteConfig === urlPartPositions.PATH && !showDefaultsConfig && !isDefaultSite)
    const pathLocale =
        (localeConfig === urlPartPositions.PATH && showDefaultsConfig) ||
        (localeConfig === urlPartPositions.PATH && !showDefaultsConfig && !isDefaultLocale)

    return (path, site, locale) => {
        const isHomeWithDefaultSiteAndLocale =
            path === HOME_HREF &&
            (defaultSite.id === site || (defaultSite.alias && defaultSite.alias === site)) &&
            (defaultLocale.id === locale || (defaultLocale.alias && defaultLocale.alias === locale))

        const sitePath = pathSite && site && !isHomeWithDefaultSiteAndLocale ? `/${site}` : ''
        const localePath =
            pathLocale && locale && !isHomeWithDefaultSiteAndLocale ? `/${locale}` : ''

        const hasQuery = isQuery && (site || locale) && !isHomeWithDefaultSiteAndLocale
        let queryString = ''
        if (hasQuery) {
            const searchParams = new URLSearchParams()
            querySite && site && searchParams.append('site', site)
            queryLocale && locale && searchParams.append('locale', locale)
            queryString = `?${searchParams.toString()}`
        }
        return `${sitePath}${localePath}${path}${queryString}`
    }
}

/*
 * Remove query params from a give url path based on a given list of keys
 *
 * @param {string} path - The part of url to have params removed from.
 * @param {array} keys - list of params to be removed
 * @returns {string} - the url after param has been removed
 * @example
 * import {removeQueryParamsFromPath} from /path/to/util/url
 *
 * removeQueryParamsFromPath(
 *   /en-GB/cart?pid=1234&color=black&size=s&abc=12,
 *   ['pid', 'color', 'size']
 * )
 * // returns
 * // '/en-GB/cart?abc=12'
 */
export const removeQueryParamsFromPath = (path, keys) => {
    const [pathname, search] = path.split('?')
    const params = new URLSearchParams(search)
    keys.forEach((key) => {
        if (params.has(key)) {
            params.delete(key)
        }
    })

    // Clean up any trailing `=` for params without values.
    const paramStr = params.toString().replace(/=&/g, '&').replace(/=$/, '')

    return `${pathname}${paramStr && '?'}${paramStr}`
}

/*
 * Remove site alias and locale from a given url, to be used for "navigate" urls
 *
 * @param {string} pathName - The part of url to have site alias and locale removed from
 * @returns {string} - the path after site alias and locale have been removed
 * @example
 * import {removeSiteLocaleFromPath} from /path/to/util/url
 *
 * removeSiteLocaleFromPath(/RefArch/en-US/account/wishlist)
 * // returns '/account/wishlist'
 */
export const removeSiteLocaleFromPath = (pathName = '') => {
    let {siteRef, localeRef} = getParamsFromPath(pathName)

    // remove the site alias from the current pathName
    if (siteRef) {
        pathName = pathName.replace(new RegExp(`/${siteRef}`, 'g'), '')
    }
    // remove the locale from the current pathName
    if (localeRef) {
        pathName = pathName.replace(new RegExp(`/${localeRef}`, 'g'), '')
    }

    return pathName
}

const LOCALIZABLE_HREF_PATTERN = /^(\/|https?:\/\/|\/\/)/i
const NON_NAVIGABLE_HREF_PATTERN = /^(#|mailto:|tel:|javascript:)/i
const HTML_HREF_ATTR_PATTERN = /(\shref\s*=\s*)(["'])([^"']*)\2/gi

/**
 * Localize an href when it targets the current app origin.
 * Relative paths and absolute same-domain URLs are rewritten with buildUrl.
 *
 * @param {string} href
 * @param {{buildUrl: function(string): string, currentOrigin: string}} options
 * @returns {string}
 */
export const localizeSameDomainHref = (href, {buildUrl, currentOrigin}) => {
    if (!href || NON_NAVIGABLE_HREF_PATTERN.test(href) || !LOCALIZABLE_HREF_PATTERN.test(href)) {
        return href
    }

    let appOrigin
    try {
        appOrigin = new URL(currentOrigin).origin
    } catch {
        return href
    }

    let url
    try {
        url = new URL(href, currentOrigin)
    } catch {
        return href
    }

    if (url.origin !== appOrigin) {
        return href
    }

    const normalizedPath = removeSiteLocaleFromPath(url.pathname)
    const localizedPath = buildUrl(`${normalizedPath}${url.search}${url.hash}`)

    if (href.startsWith('/') && !href.startsWith('//')) {
        return localizedPath
    }

    if (href.startsWith('http://') || href.startsWith('https://')) {
        return `${url.origin}${localizedPath}`
    }

    if (href.startsWith('//')) {
        return `//${url.host}${localizedPath}`
    }

    return href
}

/**
 * Builds an absolute, locale-aware canonical URL.
 *
 * SFCC/BM supplies the pathname (often with a stale public host). We keep the
 * path, strip/rebuild the locale via buildUrl, and always use the current app
 * origin so sit.uk vs intl (and localhost) self-canonicalise.
 *
 * @param {string} canonicalUrl
 * @param {{
 *  buildUrl: function(string): string,
 *  currentOrigin: string,
 *  currentPath?: string,
 *  localeRef?: string
 * }} options
 * @returns {string}
 */
export const localizeCanonicalUrl = (
    canonicalUrl,
    {buildUrl, currentOrigin, currentPath = '/', localeRef}
) => {
    if (typeof buildUrl !== 'function') {
        return canonicalUrl || ''
    }

    let appOrigin
    try {
        appOrigin = new URL(currentOrigin).origin
    } catch {
        return canonicalUrl || ''
    }

    let url
    try {
        url = new URL(canonicalUrl || currentPath || '/', appOrigin)
    } catch {
        url = new URL(currentPath || '/', appOrigin)
    }

    let normalizedPath = url.pathname || '/'
    try {
        normalizedPath = removeSiteLocaleFromPath(url.pathname) || '/'
    } catch {
        // Keep the source path when multisite configuration is unavailable.
    }
    if (localeRef) {
        const normalizedLocaleRef = String(localeRef).replace(/^\/+|\/+$/g, '')
        if (
            normalizedPath === `/${normalizedLocaleRef}` ||
            normalizedPath.startsWith(`/${normalizedLocaleRef}/`)
        ) {
            normalizedPath = normalizedPath.slice(normalizedLocaleRef.length + 1) || '/'
            if (!normalizedPath.startsWith('/')) {
                normalizedPath = `/${normalizedPath}`
            }
        }
    }
    const localizedPath = buildUrl(normalizedPath)
    const localizedUrl = new URL(localizedPath, appOrigin)

    return `${localizedUrl.origin}${localizedUrl.pathname}`
}

/**
 * Localize same-domain anchor hrefs inside SEO HTML content at render time.
 *
 * @param {string} html
 * @param {{buildUrl: function(string): string, appOrigin?: string}} options
 * @returns {string}
 */
export const localizeSeoHtmlLinks = (html, {buildUrl, appOrigin}) => {
    if (!html || typeof html !== 'string') {
        return html
    }

    const currentOrigin = appOrigin || getAppOrigin()

    return html.replace(HTML_HREF_ATTR_PATTERN, (match, prefix, quote, href) => {
        const localizedHref = localizeSameDomainHref(href, {buildUrl, currentOrigin})
        return `${prefix}${quote}${localizedHref}${quote}`
    })
}
